import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  makeStyles,
  shorthands,
  Button,
  ToolbarButton,
  Input,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Spinner,
  Text,
  tokens,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import {
  Person20Regular,
  AppsList20Regular,
  Dismiss20Regular,
  Search20Regular,
} from '@fluentui/react-icons';
import { apiConfig } from '../authConfig';
import { useAuth } from '../hooks/useAuth';

const useStyles = makeStyles({
  menuContent: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: '300px',
    maxWidth: '350px',
  },
  searchBoxWrapper: {
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  searchBox: {
    width: '100%',
    minWidth: 0,
  },
  searchControls: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  menuListWrapper: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '300px',
    overflowY: 'auto',
    overflowX: 'hidden',
    minWidth: 0,
    width: '100%',
    maxWidth: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  optionItem: {
    minWidth: 0,
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    '& [class*="fui-MenuItem__content"]': {
      minWidth: 0,
      maxWidth: '100%',
      overflow: 'hidden',
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100px',
    ...shorthands.padding(tokens.spacingVerticalM),
  },
  emptyState: {
    padding: tokens.spacingVerticalM,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    ...shorthands.padding(tokens.spacingHorizontalM),
  },
  errorMessage: {
    color: tokens.colorPaletteRedForeground1,
    ...shorthands.padding(tokens.spacingHorizontalM, tokens.spacingVerticalS),
  },
  triggerButton: {
    minWidth: '92px',
    maxWidth: 'none',
    width: 'fit-content',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    '& [class*="fui-Button__content"]': {
      overflow: 'visible',
      textOverflow: 'clip',
      whiteSpace: 'nowrap',
    },
  },
  footerBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalM),
  },
  itemCount: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  clearActionButton: {
    minHeight: '24px',
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  noFilterOption: {
    color: tokens.colorNeutralForeground4,
  },
  groupHeader: {
    ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalM),
    backgroundColor: tokens.colorNeutralBackground3,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    pointerEvents: 'none',
    userSelect: 'none',
  },
  groupedItem: {
    paddingLeft: tokens.spacingHorizontalXL,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  optionText: {
    display: 'block',
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

/**
 * SuggestionPickerMenu - 按钮+下拉菜单选择器 (MenuButton 方式)
 * 支持输入 alias，以及搜索和选择 test type
 * 
 * @param {string} type - 类型: 'alias' 或 'testType'
 * @param {string} value - 当前选中的值
 * @param {function} onSelect - 选择后的回调函数 (value) => void
 * @param {string} buttonLabel - 按钮标签，默认为 "Select"
 * @param {string} title - 菜单标题
 */
export function SuggestionPickerModal({ 
  type = 'alias', 
  value = '', 
  onSelect, 
  buttonLabel = 'Select',
  title = 'Select an item'
}) {
  const classes = useStyles();
  const { getAccessToken, accounts } = useAuth();
  const currentUserAlias = accounts[0]?.username?.split('@')[0] || '';
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [error, setError] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const menuListRef = useRef(null);
  const inputRef = useRef(null);

  // Alias options are local. Only test types need to be fetched when the menu opens.
  useEffect(() => {
    if (!open) return;

    if (type === 'alias') {
      setSuggestions(currentUserAlias ? [currentUserAlias] : []);
      setLoading(false);
      setError('');
      setHighlightIndex(0);
      return;
    }

    const fetchSuggestions = async () => {
      setLoading(true);
      setError('');
      try {
        const token = await getAccessToken();
        const response = await fetch(`${apiConfig.apiBaseUrl}/polaris/test-types`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error(`API error: ${response.status}`);
        
        const data = await response.json();
        const items = Array.isArray(data)
          ? data
              .filter((t) => t && t.IsEnabled !== false)
              .map((t) => ({ name: t.Id || t.TestType || '', team: t.Group || 'Ungrouped' }))
              .filter((t) => t.name)
          : (data.testTypes || []).map((name) => ({ name, team: 'Ungrouped' }));
        setSuggestions(items || []);
        setHighlightIndex(0);
      } catch (err) {
        setError(`Failed to load suggestions: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(fetchSuggestions, 0);
    return () => clearTimeout(timer);
  }, [open, type, currentUserAlias, getAccessToken]);

  // Filter suggestions based on search text
  const filteredSuggestions = type === 'alias'
    ? suggestions
    : (() => {
        const q = searchText.trim().toLowerCase();
        const source = suggestions;
        if (!q) return source;
        return source.filter((s) => (s.name || '').toLowerCase().includes(q));
      })();

  const flatSuggestionValues = type === 'alias'
    ? filteredSuggestions
    : filteredSuggestions.map((s) => s.name);

  const groupedTestTypeSuggestions = type === 'testType'
    ? filteredSuggestions.reduce((acc, item) => {
        const team = item.team || 'Ungrouped';
        if (!acc[team]) acc[team] = [];
        acc[team].push(item);
        return acc;
      }, {})
    : {};

  const handleSelect = useCallback((selectedValue) => {
    onSelect(selectedValue);
    setOpen(false);
    setSearchText('');
  }, [onSelect]);
  const handleClear = useCallback(() => {
    handleSelect('');
  }, [handleSelect]);

  const normalizedSearchText = searchText.trim();
  const canUseTypedValue = normalizedSearchText.length > 0;

  const focusSuggestionList = useCallback(() => {
    const listEl = menuListRef.current;
    if (!listEl) return;

    const current = listEl.querySelector(`[data-suggestion-index="${highlightIndex}"]`);
    const firstSuggestion = listEl.querySelector('[data-suggestion-index]');
    const useTypedItem = listEl.querySelector('[data-use-typed="true"]');
    const firstEnabled = listEl.querySelector('[role="menuitem"]:not([aria-disabled="true"])');

    const target = current || firstSuggestion || useTypedItem || firstEnabled;
    if (target && typeof target.focus === 'function') {
      target.focus();
    }
  }, [highlightIndex]);

  const handleListKeyDown = useCallback((e) => {
    if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.nativeEvent?.stopImmediatePropagation === 'function') {
        e.nativeEvent.stopImmediatePropagation();
      }
      inputRef.current?.focus();
    }
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Tab' && type !== 'alias') {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.nativeEvent?.stopImmediatePropagation === 'function') {
        e.nativeEvent.stopImmediatePropagation();
      }
      focusSuggestionList();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(prev => 
        prev < flatSuggestionValues.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(prev => 
        prev > 0 ? prev - 1 : flatSuggestionValues.length - 1
      );
    } else if (e.key === 'Enter' && canUseTypedValue) {
      e.preventDefault();
      handleSelect(normalizedSearchText);
    } else if (e.key === 'Enter' && flatSuggestionValues.length > 0) {
      e.preventDefault();
      handleSelect(flatSuggestionValues[highlightIndex]);
    }
  };

  const typeMeta = type === 'alias'
    ? { label: 'User', searchPlaceholder: 'Enter user alias...' }
    : { label: 'Type', searchPlaceholder: 'Search type...' };
  const displayValue = value ? value : (buttonLabel === 'Select' ? typeMeta.label : buttonLabel);
  const triggerIcon = type === 'alias' ? <Person20Regular /> : <AppsList20Regular />;
  const triggerTitle = value
    ? `${typeMeta.label} filter: ${value}`
    : (title === 'Select an item' ? `Select ${typeMeta.label}` : title);

  return (
    <Menu open={open} onOpenChange={(e, data) => setOpen(data.open)}>
      <MenuTrigger disableButtonEnhancement>
        <ToolbarButton
          icon={triggerIcon}
          title={triggerTitle}
          className={classes.triggerButton}
        >
          {displayValue}
        </ToolbarButton>
      </MenuTrigger>

      <MenuPopover style={{ padding: 0 }}>
        <div className={classes.menuContent}>
          {error && (
            <div className={classes.errorMessage}>
              <MessageBar intent="error" compact>
                <MessageBarBody>{error}</MessageBarBody>
              </MessageBar>
            </div>
          )}
          
          <div className={classes.searchBoxWrapper}>
            <div className={classes.searchControls}>
              <Input
                ref={inputRef}
                className={classes.searchBox}
                placeholder={typeMeta.searchPlaceholder}
                value={searchText}
                onChange={(e, d) => {
                  setSearchText(d.value);
                  setHighlightIndex(0);
                }}
                onKeyDown={handleKeyDown}
                clearable
              />
              {type === 'alias' && (
                <Button
                  appearance="primary"
                  icon={<Search20Regular />}
                  disabled={!canUseTypedValue}
                  onClick={() => handleSelect(normalizedSearchText)}
                >
                  Search
                </Button>
              )}
            </div>
          </div>

          {loading ? (
            <div className={classes.loadingContainer}>
              <Spinner size="small" label="Loading..." />
            </div>
          ) : (
            <>
              <MenuList className={classes.menuListWrapper} ref={menuListRef} onKeyDown={handleListKeyDown}>
                <MenuItem
                  className={`${classes.optionItem} ${classes.noFilterOption}`}
                  onClick={handleClear}
                  checked={!value}
                >
                  <span className={classes.optionText} title={`(No ${typeMeta.label} filter)`}>(No {typeMeta.label} filter)</span>
                </MenuItem>
                {type !== 'alias' && canUseTypedValue && (
                  <MenuItem className={classes.optionItem} data-use-typed="true" onClick={() => handleSelect(normalizedSearchText)}>
                    <span className={classes.optionText} title={`Use "${normalizedSearchText}"`}>Use "{normalizedSearchText}"</span>
                  </MenuItem>
                )}
                {flatSuggestionValues.length === 0 ? (
                  type === 'alias' ? null : <MenuItem className={classes.optionItem} disabled>
                    <span
                      className={classes.optionText}
                      title={suggestions.length === 0
                        ? 'No suggestions available'
                        : searchText ? 'No matching results (press Enter to use current input)' : 'No items found'}
                    >
                      {suggestions.length === 0
                        ? 'No suggestions available'
                        : searchText ? 'No matching results (press Enter to use current input)' : 'No items found'}
                    </span>
                  </MenuItem>
                ) : (
                  type === 'alias'
                    ? filteredSuggestions.map((item, index) => (
                      <MenuItem
                        className={classes.optionItem}
                        key={item}
                        data-suggestion-index={index}
                        onClick={() => handleSelect(item)}
                        checked={item === value}
                      >
                        <span className={classes.optionText} title={item}>{item}</span>
                      </MenuItem>
                    ))
                    : (() => {
                        let flatIndex = 0;
                        return Object.keys(groupedTestTypeSuggestions)
                          .sort((a, b) => a.localeCompare(b))
                          .map((team) => (
                            <React.Fragment key={team}>
                              <div className={classes.groupHeader}>{team}</div>
                              {groupedTestTypeSuggestions[team].map((item) => {
                                const index = flatIndex;
                                flatIndex += 1;
                                return (
                                  <MenuItem
                                    key={`${team}:${item.name}`}
                                    className={`${classes.optionItem} ${classes.groupedItem}`}
                                    data-suggestion-index={index}
                                    onClick={() => handleSelect(item.name)}
                                    checked={item.name === value}
                                  >
                                    <span className={classes.optionText} title={item.name}>{item.name}</span>
                                  </MenuItem>
                                );
                              })}
                            </React.Fragment>
                          ));
                      })()
                )}
              </MenuList>
            </>
          )}

          {!loading && (
            <div className={classes.footerBar}>
              <Text className={classes.itemCount}>
                {flatSuggestionValues.length} item{flatSuggestionValues.length !== 1 ? 's' : ''}
              </Text>
              {Boolean(value) && (
                <ToolbarButton
                  className={classes.clearActionButton}
                  icon={<Dismiss20Regular />}
                  onClick={handleClear}
                  size="small"
                >
                  Clear
                </ToolbarButton>
              )}
            </div>
          )}
        </div>
      </MenuPopover>
    </Menu>
  );
}

export default SuggestionPickerModal;
